/**
 * Línea fina arriba de la pantalla mientras la página que se acaba de abrir trae sus datos.
 *
 * Las rutas no se cargan de forma perezosa (todo el código viene en el mismo paquete), así que lo que
 * tarda al cambiar de página son sus datos: la línea se liga a la **primera carga** de las consultas
 * marcadas con `meta: { cargaDePagina: true }` (las principales de cada página: tareas, personas,
 * tokens). Los refrescos (polling de 30 s) no la muestran porque esas consultas ya tienen datos.
 * Como los esqueletos, aparece solo si la carga tarda más de ~300 ms. Es decorativa (`aria-hidden`):
 * la zona que carga ya lleva `aria-busy` y su texto para lectores.
 */
import { useIsFetching } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useMostrarTrasRetardo } from './useMostrarTrasRetardo';
import './BarraCargaPagina.css';

/** `meta` que marca una consulta como «la de la página» (ver arriba). */
export const META_CARGA_DE_PAGINA = { cargaDePagina: true } as const;

type Fase = 'oculta' | 'cargando' | 'terminando';
const DURACION_FINAL_MS = 450;

export function BarraCargaPagina() {
  const enCurso = useIsFetching({
    predicate: (consulta) =>
      consulta.meta?.cargaDePagina === true && consulta.state.data === undefined,
  });
  const mostrar = useMostrarTrasRetardo(enCurso > 0);
  const [fase, setFase] = useState<Fase>('oculta');
  const [anterior, setAnterior] = useState(mostrar);

  if (mostrar !== anterior) {
    setAnterior(mostrar);
    setFase(mostrar ? 'cargando' : 'terminando');
  }

  // Al terminar, la línea llega al final y se desvanece; luego vuelve a cero sin animación.
  useEffect(() => {
    if (fase !== 'terminando') return;
    const temporizador = window.setTimeout(() => setFase('oculta'), DURACION_FINAL_MS);
    return () => window.clearTimeout(temporizador);
  }, [fase]);

  return (
    <div className={`barra-pagina barra-pagina--${fase}`} aria-hidden="true">
      <i />
    </div>
  );
}
